import type { Layer, Selection } from '@geosoft/viewer-sdk';
import { Button, Card, Chip, Icon, Stack, Text } from '@/design';
import { isThreeDLayer } from './model';

interface ThreeDSelectionDetailProps {
  selection: Selection | null;
  selectedLayer: Layer | null;
}

function buildPivotId(selection: Selection | null): string {
  if (!selection) return 'none';
  if (selection.featureId == null) return selection.layerId;
  return `${selection.layerId}:${String(selection.featureId)}`;
}

export function ThreeDSelectionDetail({ selection, selectedLayer }: ThreeDSelectionDetailProps) {
  if (!selectedLayer || !isThreeDLayer(selectedLayer)) return null;

  const objectId = buildPivotId(selection);
  const hasMissingGeometry = objectId.toLowerCase().includes('missing');

  return (
    <Card
      level={2}
      header="3D object detail"
      data-testid="three-d-selection-detail"
      footer={
        <Stack direction="row" align="center" gap={1} style={{ flexWrap: 'wrap' }}>
          <Chip icon="view_in_ar" label={selectedLayer.kind === 'bim' ? 'BIM' : '3D Tiles'} variant="neutral" />
          <Chip icon="sell" label={`Pivot id: ${objectId}`} variant="neutral" />
        </Stack>
      }
      actions={
        <>
          <Button variant="ghost" aria-label="Open linked drawings">
            <Icon name="architecture" size={20} decorative />
            <span>Drawings</span>
          </Button>
          <Button variant="ghost" aria-label="Open linked map location">
            <Icon name="map" size={20} decorative />
            <span>Map</span>
          </Button>
          <Button variant="ghost" aria-label="Open linked registers">
            <Icon name="table" size={20} decorative />
            <span>Registers</span>
          </Button>
        </>
      }
    >
      <Stack direction="column" gap={1}>
        <Text as="p" variant="body" style={{ margin: 0 }}>
          {selectedLayer.label ?? selectedLayer.id}
        </Text>
        <Text as="p" variant="caption" style={{ margin: 0 }}>
          Layer id: {selectedLayer.id}
        </Text>
        <Text as="p" variant="caption" style={{ margin: 0 }}>
          Feature id: {selection?.featureId != null ? String(selection.featureId) : 'none'}
        </Text>
        {hasMissingGeometry ? (
          <Chip icon="deployed_code_alert" label="Missing geometry mapping" variant="warning" />
        ) : (
          <Chip icon="check_circle" label="Mapped geometry" variant="success" />
        )}
      </Stack>
    </Card>
  );
}
